import { SphereTooltip } from "@components/common/sphere-tooltip";
import { GridColDef, GridRenderCellParams } from "@mui/x-data-grid-pro";
import { withEllipsis } from "@styles/common-styles";
// Here we do not need it as a React component because will be used as src for an image
import defaultGroupThumbnail from "@assets/group-thumbnail_default.svg";
import { GroupTypes } from "@custom-types/group-types";
import { isGroupDetails } from "@custom-types/type-guards";
import { FormatDate } from "@hooks/use-date-time";
import { getProjectNumbersInGroup } from "@utils/group-utils";
import { DateTime } from "luxon";
import { GroupHeaders } from "@components/groups/groups-constants";

interface Props {
  // TODO: Extract it in separate file or access in in the component: https://faro01.atlassian.net/browse/ST-1348
  options: {
    /** Whether the current screen is large or larger */
    isScreenLgOrLarger: boolean;

    /** Whether the current screen is medium */
    isScreenMd: boolean;
  };

  formatDate: FormatDate;
}

/** The default padding for the columns in pixels */
const COL_DEFAULT_X_PADDING = 16;

/** The width of the thumbnail image in pixels */
const THUMBNAIL_IMAGE_WIDTH = 70;

/** Minimum column width in px */
const COLS_MIN_WIDTH_IN_PX = 100;

/** Column width in px for medium or smaller screen */
const MD_OR_SMALLER_COLUMNS_WIDTH_IN_PX = 200;

/** Column width in px for large and larger screens */
const LG_OR_LARGER_COLUMNS_WIDTH_IN_PX = 250;

/** Return all the possible columns for Groups table */
export function getGroupsColumns({
  options,
  formatDate,
}: Props): Record<GroupHeaders, GridColDef> {
  const maxTextColumnWidth = options.isScreenLgOrLarger
    ? LG_OR_LARGER_COLUMNS_WIDTH_IN_PX
    : MD_OR_SMALLER_COLUMNS_WIDTH_IN_PX;

  const flexSetting = options.isScreenLgOrLarger ? 1 : undefined;

  return {
    [GroupHeaders.thumbnail]: {
      field: GroupHeaders.thumbnail,
      headerName: "",
      width: 190,
      // Use two times COL_DEFAULT_X_PADDING, one for each side
      minWidth: THUMBNAIL_IMAGE_WIDTH + 2 * COL_DEFAULT_X_PADDING,
      renderCell: (params: GridRenderCellParams<{ entity: GroupTypes }>) => {
        return (
          <img
            // Don't use Nullish coalescing (??) as it might happen that the thumbnailUrl is an empty string
            src={
              params.row.entity.thumbnailUrl
                ? params.row.entity.thumbnailUrl
                : defaultGroupThumbnail
            }
            style={{
              height: "50px",
              width: "70px",
              maxHeight: "100%",
              maxWidth: "100%",
              objectFit: "contain",
            }}
            alt="Group thumbnail"
          />
        );
      },
    },

    [GroupHeaders.name]: {
      field: GroupHeaders.name,
      flex: flexSetting,
      minWidth: COLS_MIN_WIDTH_IN_PX,
      maxWidth: 380,
      renderCell: (params: GridRenderCellParams<{ entity: GroupTypes }>) => {
        return (
          <SphereTooltip
            title={<var>{params.row.entity.name}</var>}
            boxProps={{
              sx: {
                ...withEllipsis,
                fontWeight: "bold",
              },
            }}
          >
            <var>{params.row.entity.name}</var>
          </SphereTooltip>
        );
      },
    },

    [GroupHeaders.members]: {
      field: GroupHeaders.members,
      width: MD_OR_SMALLER_COLUMNS_WIDTH_IN_PX,
      minWidth: 54,
      renderCell: (params: GridRenderCellParams<{ entity: GroupTypes }>) => {
        if (!isGroupDetails(params.row.entity)) {
          return <var>{params.row.entity.numManagers}</var>;
        }
      },
    },

    [GroupHeaders.projects]: {
      field: GroupHeaders.projects,
      flex: flexSetting,
      width: maxTextColumnWidth,
      minWidth: COLS_MIN_WIDTH_IN_PX,
      renderCell: (params: GridRenderCellParams<{ entity: GroupTypes }>) => {
        return <var>{getProjectNumbersInGroup(params.row.entity)}</var>;
      },
    },

    [GroupHeaders.createdDate]: {
      field: GroupHeaders.createdDate,
      flex: flexSetting,
      minWidth: COLS_MIN_WIDTH_IN_PX,
      maxWidth: maxTextColumnWidth,
      renderCell: (params: GridRenderCellParams<{ entity: GroupTypes }>) => {
        return (
          <var>
            {formatDate(params.row.entity.createdAt, DateTime.DATETIME_MED)}
          </var>
        );
      },
    },

    // A placeholder for further options
    [GroupHeaders.options]: {
      field: GroupHeaders.options,
      headerName: "",
      width: 95,
      renderCell: () => {
        return "";
      },
    },
  };
}
